import React, { useState } from "react";
import { CircularProgress, Dialog, Tooltip } from "@mui/material";
import toast from "react-hot-toast";
import {
  CloseRounded,
  QuestionMark,
  Psychology,
  SportsEsports,
  Person,
} from "@mui/icons-material";

import { ReactComponent as AdminPlayer } from "../../assets/icons/adminPlayer.svg";

import SoftButton from "../../components/SoftButton";
import SoftTypography from "../../components/SoftTypography";
import SoftBox from "../../components/SoftBox";
import { PUT_USER } from "../../api/user";
import ERRORS from "../../constants/errorStatus";
import UpgradePopUp from "../../components/ChallengeArenaPopUp/UpgradePopUp";
import { useDispatch } from "react-redux";
import { getCompanyUsers } from "../../redux/company/actions";
import { fontSize, width } from "@mui/system";

const GradientCircle = () => (
  <svg width="17" height="15" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.6538 7.5C15.6538 11.3317 12.2971 14.5 8.07692 14.5C3.85676 14.5 0.5 11.3317 0.5 7.5C0.5 3.66826 3.85676 0.5 8.07692 0.5C12.2971 0.5 15.6538 3.66826 15.6538 7.5Z" stroke="url(#paint0_linear_750_7465)"/>
    <ellipse cx="8.0769" cy="7.5" rx="5.9231" ry="5.50002" fill="url(#paint1_linear_750_7465)"/>
    <defs>
    <linearGradient id="paint0_linear_750_7465" x1="13.1171" y1="59.4771" x2="-3.98113" y2="58.3098" gradientUnits="userSpaceOnUse">
    <stop stopColor="#7928CA"/>
    <stop offset="1" stopColor="#FF0080"/>
    </linearGradient>
    <linearGradient id="paint1_linear_750_7465" x1="11.7731" y1="45.6167" x2="-0.765693" y2="44.7606" gradientUnits="userSpaceOnUse">
    <stop stopColor="#7928CA"/>
    <stop offset="1" stopColor="#FF0080"/>
    </linearGradient>
    </defs>
  </svg>
);

const EmptyCircle = () => (
  <svg width="17" height="15" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.6538 7.5C15.6538 11.3317 12.2971 14.5 8.07692 14.5C3.85676 14.5 0.5 11.3317 0.5 7.5C0.5 3.66826 3.85676 0.5 8.07692 0.5C12.2971 0.5 15.6538 3.66826 15.6538 7.5Z" stroke="#98A2B3"/>
  </svg>
);

const UpdatePlayerRole = ({ user, isLoggedInUser, isFirstAdmin }) => {
  const [updateRole, setUpdateRole] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user.isAdmin ? 'admin' : 'player');

  const openRemoveDialog = () => setUpdateRole(true);
  const closeUpdateRoleDialog = () => setUpdateRole(false);

  const openUpgradeDialog = () => setShowUpgrade(true);
  const closeUpgradeDialog = () => setShowUpgrade(false);

  const dispatch = useDispatch();

  const handleAdmin = async (id, admin) => {
    setLoading(true);
    const action = admin ? "Remov" : "Add";

    try {
      await PUT_USER(id, { isAdmin: !admin }, false);
      setLoading(true);
      closeUpdateRoleDialog();
      setTimeout(() => {
        dispatch(getCompanyUsers());
      }, 1000);

      toast.success(`Admin Successfully ${action}ed`);
    } catch (err) {
      setLoading(false);
      if (err?.response?.status === ERRORS.UPGRADE_REQUIRED) {
        openUpgradeDialog();
      }
      toast.error(err?.response?.data?.message || "Unable to update role");
    }
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  return (
    <>
        <SoftButton
          size="small"
          color="primary"
          onClick={!isFirstAdmin ? openRemoveDialog : undefined}
          sx={{
            minWidth: 0,
            width: "auto",
            height: "26px",
            borderRadius: "999px",
            border: "none",
            background: user.isAdmin
              ? "#FEF6EE !important"
              : "var(--gmb-primary-soft, #EAF1F8) !important",
            color: user.isAdmin ? "#B93815" : "var(--gmb-primary, #1B598E)",
            boxShadow: "none !important",
            opacity: "1 !important",
            fontFamily: "DM Sans",
            fontSize: "11.5px",
            fontWeight: 500,
            textTransform: "none",
            letterSpacing: "0em",
            padding: "0 12px",
            cursor: isFirstAdmin ? "default" : "pointer",
            "&:hover": {
              background: user.isAdmin ? "#FEF6EE !important" : "var(--gmb-primary-tint, #D8E5F0) !important",
            },
          }}
        >
          {user.isAdmin ? "Admin" : "Player"}
        </SoftButton>
      {updateRole && (
        <Dialog
          open={updateRole}
          onClose={closeUpdateRoleDialog}
          sx={{
            "& .MuiPaper-root": {
              padding: "1rem",
              borderRadius: "24px",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.70) 0%, rgba(255,255,255,0.60) 100%)",
              boxShadow:
                "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
              backdropFilter: "blur(18px) saturate(160%)",
              width: "100%",
              maxWidth: "500px",
              border: "1px solid rgba(255,255,255,0.6)",
            },
          }}
        >
          <SoftBox sx={{ padding: { mini: "1rem", md: "1.5rem" } }}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftTypography
                component="h4"
                sx={{
                  fontWeight: 500,
                  color: "#0F172A",
                  fontSize: 20,
                  textAlign: "center",
                }}
              >
                Edit {user.firstName} {user.lastName} Role
              </SoftTypography>
              <SoftButton
                variant="outlined"
                color="dark"
                onClick={closeUpdateRoleDialog}
                sx={{
                  "& svg": { fontSize: "1.25rem !important" },
                  borderRadius: "50%",
                  minWidth: "max-content",
                  minHeight: "max-content",
                  padding: "0.25rem",
                  color: "#475467",
                }}
              >
                <CloseRounded />
              </SoftButton>
            </SoftBox>
          <SoftBox paddingX={3} paddingBottom={3} paddingTop={1} sx={{mt: "4rem"}}>
            <SoftBox display="flex" flexDirection="column" gap="0.5rem">
                <SoftButton
                  onClick={() => handleRoleSelect('player')}
                  sx={{
                    width: "100%",
                    background:"rgba(15,23,42,0.03)",
                    borderRadius: "8px",
                    padding: "0.875rem 1rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0px 3px 5px 0px rgba(0, 0, 0, 0.05)",
                    "&:hover": {
                      background: "rgba(15,23,42,0.06)",
                    },
                    "&:focus:not(:hover)": {
                      background: "rgba(15,23,42,0.06)",
                    },
                  }}
                >
                  <SoftTypography variant="h6" sx={{ color: "#0F172A" }}>
                    Player Role
                  </SoftTypography>
                  {selectedRole === 'player' ? <GradientCircle /> : <EmptyCircle />}
                </SoftButton>
            </SoftBox>
            <SoftBox display="flex" flexDirection="column" gap="0.5rem" sx={{ mt: "1rem" }}>
                <SoftButton
                  onClick={() => handleRoleSelect('admin')}
                  sx={{
                    width: "100%",
                    background:"rgba(15,23,42,0.03)",
                    borderRadius: "8px",
                    padding: "0.875rem 1rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0px 3px 5px 0px rgba(0, 0, 0, 0.05)",
                    "&:hover": {
                      background: "rgba(15,23,42,0.06)",
                    },
                    "&:focus:not(:hover)": {
                      background: "rgba(15,23,42,0.06)",
                    },
                  }}
                >
                  <SoftTypography variant="h6" sx={{ color: "#0F172A" }}>
                    Admin Role
                  </SoftTypography>
                  {selectedRole === 'admin' ? <GradientCircle /> : <EmptyCircle />}
                </SoftButton>
            </SoftBox>
            <SoftBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginTop="2rem"
            >
              <SoftButton
                variant="contained"
                color="primary"
                onClick={() => handleAdmin(user.userId, user.isAdmin)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  padding: "0.875rem 1rem",
                  background: "var(--gmb-primary, #1B598E)",
                  color: "#FFFFFF",
                  fontWeight: "800",
                  fontSize: "15px",
                  width: "50%",
                  "&:hover": {
                    background: "var(--gmb-primary, #1B598E)",
                  },
                  "&:focus:not(:hover)": {
                    background: "var(--gmb-primary, #1B598E)",
                  },
                }}
              >
                Update
              </SoftButton>
            </SoftBox>
          </SoftBox>
          </SoftBox>
        </Dialog>
      )}
      {showUpgrade && (
        <UpgradePopUp
          showUpgrade={showUpgrade}
          closeUpgrade={closeUpgradeDialog}
        />
      )}
    </>
  );
};

export default UpdatePlayerRole;
